// React & Hooks
import { useState, useEffect } from "react";

// Styling
import styled from "styled-components";
// Routing & State
import { useHistory, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Assets
import logo from "../../images/newLogo.png";

// Icon Components
import FacebookIcon from "../../icons/FacebookIcon";
import InstagramIcon from "../../icons/InstagramIcon";
import YoutubeIcon from "../../icons/YoutubeIcon";
import ChevronDownIcon from "../../icons/ChevronDownIcon";
import Heart2 from "../../icons/Heart2";
import Search2 from "../../icons/Search2";

// Components
import H1 from "../H1/H1";
import CustomMenu from "../CustomMenu/CustomMenu";
import UpperNav from "../Nav/UpperNav";
import MyModal from "./MyModal";
import Search from "./Search";

// Constants
import {
  SUPPORTED_LOCALS,
  MENU_CONFIG,
  SOCIAL_MEDIA_URLS,
} from "../../constants";
import { setLocal } from "../../actions/local";
import { useFetchCities } from "../../hooks/useFetchCities";

// Utils
import isMobile from "ismobilejs";

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0rem 7.625rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  height: 114px;
  position: relative;
  top: 39px; /* Position correctly below UpperNav on desktop */
  left: 0;
  right: 0;
  background: white;

  @media (max-width: 768px) {
    top: 0; /* On mobile, no UpperNav so start at container top */
  }

  @media (min-width: 800px) and (max-width: 1400px) {
    height: 100px;
  }
  @media (min-width: 1401px) and (max-width: 1800px) {
    height: 111px;
  }
  @media (max-width: 768px) {
    padding: 0rem 2rem;
    height: 80px;
  }
  padding-bottom: -23.5px;

  .img {
    z-index: 99999;
    position: absolute;
    background: white;
    cursor: pointer;
    width: 271px;
    height: 96px;
    left: 50%;
    transform: translateX(-50%);
    @media (max-width: 768px) {
      width: 200px;
      height: 70px;
      top: 5px;
    }
  }
  .flex-start,
  .flex-end {
    > *:hover {
      /* text-decoration: underline !important; */
    }
  }
  .eleme {
    display: -webkit-flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    @media (max-width: 768px) {
      gap: 4px;
    }
    div {
      color: #737373 !important;

      :hover {
        color: ${({ theme }) => theme.green} !important;
      }
    }

    img {
      transition: filter 0.3s ease;
    }

    &:hover {
      img {
        filter: brightness(0) saturate(100%) invert(22%) sepia(28%)
          saturate(1084%) hue-rotate(128deg) brightness(91%) contrast(92%);
      }
    }
  }
  [class^="flex"] {
    display: -webkit-flex;
    object-fit: cover;
    flex-direction: row;
    align-items: center;
    gap: 40px;
    @media (max-width: 768px) {
      gap: 20px;
    }
    > *:hover {
      cursor: pointer;
    }
  }
  .socials {
    display: -webkit-flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;

    button {
      all: unset;
      display: flex;
      cursor: pointer;
    }
  }

  /* Mobile Layout */
  @media (max-width: 768px) {
    .flex-start,
    .flex-end {
      display: none !important;
    }

    .mobile-left {
      display: flex;
      align-items: center;
      gap: 20px;
      z-index: 100000;
    }

    .mobile-right {
      display: flex;
      align-items: center;
      z-index: 100000;
    }

    .burger-menu {
      display: flex;
      flex-direction: column;
      cursor: pointer;
      width: 24px;
      height: 18px;
      justify-content: space-between;

      .burger-line {
        width: 100%;
        height: 2px;
        background-color: #1c473b;
        transition: all 0.3s ease;

        &.open {
          &:nth-child(1) {
            transform: rotate(45deg) translate(6px, 6px);
          }
          &:nth-child(2) {
            opacity: 0;
          }
          &:nth-child(3) {
            transform: rotate(-45deg) translate(6px, -6px);
          }
        }
      }
    }
  }

  @media (min-width: 769px) {
    .mobile-left,
    .mobile-right,
    .burger-menu {
      display: none !important;
    }
  }
`;

// Icon Wrapper for chevron icons
const IconWrapper = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
`;

// Icon Wrapper for heart and search icons
const ActionIconWrapper = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  cursor: pointer;

  svg path {
    transition: fill 0.3s ease;
  }

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.green};
    }
  }
`;

// Mobile Menu Modal Styles
const MobileMenuModal = styled.div`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  min-height: calc(
    100vh - 80px
  ); /* Ensures full screen coverage from navbar down */
  background: white;
  z-index: 99999; /* Higher than navbar to ensure visibility */
  transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */

  .upper-nav-section {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 20px 0;

    .social-links {
      padding: 0.8rem 28px;
      display: flex;
      align-items: center;
      gap: 8px;

      .social-item {
        display: flex;
        align-items: center;
        gap: 6px;
        text-decoration: none;
        color: #333;

        img {
          width: 16px;
          height: 16px;
        }

        p {
          margin: 0;
          font-size: 14px;
          font-family: ${({ theme }) => theme.fonts.sofia};
        }
      }

      .separator {
        width: 1px;
        height: 16px;
        background-color: rgba(0, 0, 0, 0.2);
        margin: 0 8px;
      }
    }
  }

  .menu-section {
    margin-bottom: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &.expanded {
      background-color: #1c473b;
      border-bottom: 1px solid #1c473b;

      .section-title {
        color: white;
        margin-bottom: 0;
        padding-bottom: 1rem;

        span svg path {
          fill: white; /* Make arrow white */
        }
      }

      .section-items {
        padding: 0 28px 32px 28px;
        display: flex;
        flex-direction: column;
        gap: 14px;

        .menu-item {
          .item-title {
            color: white;
          }

          .item-description {
            color: rgba(255, 255, 255, 0.6);
            opacity: 0.8;
          }

          &:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          &.active {
            background-color: rgba(255, 255, 255, 0.2);
            border-left: 3px solid white;

            .item-title {
              color: white;
              font-weight: 500;
            }

            .item-description {
              color: white;
              opacity: 0.9;
            }
          }
        }
      }
    }

    &:not(.expanded) {
      .section-items {
        padding: 0;
        height: 0;
        opacity: 0;
      }
    }

    .section-title {
      font-family: ${({ theme }) => theme.fonts.ivy};
      font-size: 20px; /* Larger size for better readability */
      font-weight: 400;
      color: ${({ theme }) => theme.green};
      margin-bottom: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.3s ease;
      padding: 32px 28px;

      span {
        transition: transform 0.3s ease;

        svg {
          transition: transform 0.3s ease;
        }
      }
    }

    .section-items {
      overflow: hidden;
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      gap: 14px;

      &.collapsed {
        height: 0;
        padding: 0;
        gap: 0;
        opacity: 0;
      }

      &.expanded {
        height: auto;
        opacity: 1;
      }

      .menu-item {
        padding: 0.8rem 0;
        cursor: pointer;

        .item-title {
          font-family: ${({ theme }) => theme.fonts.ivy};
          font-size: 18px; /* Larger size for better readability */
          font-weight: 400;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .item-description {
          font-family: ${({ theme }) =>
            theme.fonts.sofia}; /* Same as CustomMenu descriptions */
          font-size: 14px; /* Larger size for better readability */
          font-weight: 400;
          color: #666;
          line-height: 1.4;
          opacity: 0.8;
        }

        &:hover {
          background-color: rgba(116, 185, 124, 0.05);
          padding-left: 0.5rem;
          transition: all 0.2s ease;
        }

        &.active {
          background-color: rgba(116, 185, 124, 0.1);
          border-left: 3px solid ${({ theme }) => theme.green};
          padding-left: 0.5rem;

          .item-title {
            color: ${({ theme }) => theme.green};
            font-weight: 500;
          }

          .item-description {
            color: ${({ theme }) => theme.green};
            opacity: 0.7;
          }
        }
      }
    }
  }
`;

/**
 * NewNavbar Component
 *
 * A responsive navigation bar with dropdown menus for destinations, things to do, info, and plan sections.
 * Features a centered logo and social action buttons (favorites and search).
 *
 * @param {boolean} contact - Controls the logo background transparency
 * @param {function} setContact - Setter function for contact state
 */
function NewNavbar({ contact, setContact }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { local } = useSelector((state) => state.Local);
  const { data: cities = [] } = useFetchCities();
  let [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  function closeSearchModal() {
    setIsSearchModalOpen(false);
  }

  function openSearchModal() {
    setIsSearchModalOpen(true);
    // Close mobile menu if open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      setExpandedMobileSection(null);
    }
  }
  // Transform menu config to include translated labels and descriptions
  const createMenuItems = (config) => ({
    title: t(config.titleKey),
    items: config.items.map((item) => ({
      label: t(item.labelKey),
      path: item.path,
      description: t(item.descriptionKey),
      external: item.external || false,
      url: item.url,
      onClick: item.external
        ? () => window.open(item.url, "_blank")
        : undefined,
    })),
  });

  // Create destinations from cities (same as original Menu component)
  const createDestinationsFromCities = () => ({
    title: t("destinations"),
    items: cities
      .filter((city) => city.slug !== "volubilis") // Filter out volubilis like in original Menu
      .map((city) => ({
        label: city.title[local] ? city.title[local] : city.title["en"],
        path: `/${local}/destination/${city.id}`,
        description: t(`${city.slug}Description`),
        onClick: () => {
          history.push(`/${local}/destination/${city.id}`);
        },
      })),
  });

  // Menu data with translations
  const menuData = {
    destinations: [createDestinationsFromCities()],
    thingsToDo: [createMenuItems(MENU_CONFIG.thingsToDo)],
    info: [createMenuItems(MENU_CONFIG.info)],
    plan: [createMenuItems(MENU_CONFIG.plan)],
  };

  // State for dropdown menu visibility
  const [menuStates, setMenuStates] = useState({
    destinations: false,
    thingsToDo: false,
    info: false,
    plan: false,
  });

  // State for mobile burger menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // State for navbar visibility on scroll
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handlers for menu toggle
  const toggleMenu = (menuType) => {
    setMenuStates((prev) => ({
      ...prev,
      [menuType]: !prev[menuType],
    }));
  };

  const closeMenu = (menuType) => {
    setMenuStates((prev) => ({
      ...prev,
      [menuType]: false,
    }));
  };

  // Mobile menu state
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Prevent background scroll but allow mobile menu to scroll
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
      // Ensure navbar is visible when mobile menu opens
      setIsNavbarVisible(true);
    } else {
      // Restore background scroll and position
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    // Cleanup function to reset styles when component unmounts
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isMobileMenuOpen]);

  // Lock body scroll when any desktop menu is open
  useEffect(() => {
    const anyMenuOpen = Object.values(menuStates).some((state) => state);

    if (anyMenuOpen) {
      // Prevent background scroll when desktop menu is open
      document.body.style.overflow = "hidden";
      // Ensure navbar is visible when menu opens
      setIsNavbarVisible(true);
    } else {
      // Restore scroll when menu closes
      document.body.style.overflow = "";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuStates]);

  // Scroll detection effect
  useEffect(() => {
    const controlNavbar = () => {
      // Always keep navbar visible when mobile menu is open
      if (isMobileMenuOpen) {
        setIsNavbarVisible(true);
        return;
      }

      // Always keep navbar visible when any desktop dropdown menu is open
      const anyMenuOpen = Object.values(menuStates).some((state) => state);
      if (anyMenuOpen) {
        setIsNavbarVisible(true);
        return;
      }

      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        // Always show navbar at the top
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px - hide navbar
        setIsNavbarVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show navbar
        setIsNavbarVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", controlNavbar);

    return () => {
      window.removeEventListener("scroll", controlNavbar);
    };
  }, [lastScrollY, isMobileMenuOpen, menuStates]);

  // Handlers
  const handleFavoritesClick = () => {
    history.push(`/${local}/fav`);
    // Close mobile menu if open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      setExpandedMobileSection(null);
    }
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    // When opening mobile menu, ensure navbar is visible
    if (newState) {
      setIsNavbarVisible(true);
    }

    if (isMobileMenuOpen) {
      setExpandedMobileSection(null);
    }
  };

  const toggleMobileSection = (section) => {
    setExpandedMobileSection(
      expandedMobileSection === section ? null : section
    );
  };

  const handleMobileItemClick = (item) => {
    if (item.external && item.url) {
      window.open(item.url, "_blank");
    } else if (item.onClick) {
      item.onClick();
    } else {
      history.push(`/${local}${item.path}`);
    }
    setIsMobileMenuOpen(false);
    setExpandedMobileSection(null);
  };

  const handleLanguageChange = (newLocal) => {
    const currentLocation = window.location.pathname;
    const newLocation = currentLocation.replace(
      "/" + local + "/",
      "/" + newLocal + "/"
    );
    history.replace(newLocation);
    dispatch(setLocal(newLocal));
    setIsMobileMenuOpen(false);
    setExpandedMobileSection(null);
  };

  return (
    <>
      {/* UpperNav - Desktop Only - Independent z-index */}
      {!isMobile().phone && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100003,
            transform: `translateY(${
              isNavbarVisible &&
              !Object.values(menuStates).some((state) => state)
                ? "0"
                : "-60px"
            })`,
            transition: Object.values(menuStates).some((state) => state)
              ? "transform 0s" // No transition when menu is open - instant hide
              : "transform 0.3s ease-in-out", // Normal transition when no menu is open
          }}
        >
          <UpperNav />
        </div>
      )}

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          transform: `translateY(${
            isNavbarVisible ? "0" : isMobile().phone ? "-80px" : "-155px"
          })`,
          transition: "transform 0.3s ease-in-out",
        }}
      >
        <Wrapper isVisible={true}>
          {/* Desktop Left Navigation Section */}
          <div className="flex-start">
            <div className="eleme" onClick={() => toggleMenu("destinations")}>
              <H1 small boldish>
                {t("destinations")}
              </H1>
              <IconWrapper>
                <ChevronDownIcon width={12} height={12} />
              </IconWrapper>
            </div>
            <div className="eleme" onClick={() => toggleMenu("thingsToDo")}>
              <H1 small boldish>
                {t("thingsToDo")}
              </H1>
              <IconWrapper>
                <ChevronDownIcon width={12} height={12} />
              </IconWrapper>
            </div>
          </div>

          {/* Mobile Left Section - Search & Favorites */}
          <div className="mobile-left">
            <ActionIconWrapper onClick={openSearchModal}>
              <Search2 width={24} height={24} />
            </ActionIconWrapper>
            <ActionIconWrapper onClick={handleFavoritesClick}>
              <Heart2 width={24} height={24} />
            </ActionIconWrapper>
          </div>

          {/* Center Logo */}
          <Link
            to={`/${local}?`}
            className="img"
            style={contact ? { background: "transparent" } : {}}
          >
            <img src={logo} alt={t("logoAlt")} />
          </Link>

          {/* Desktop Right Navigation Section */}
          <div className="flex-end">
            <div className="eleme" onClick={() => toggleMenu("info")}>
              <H1 small boldish>
                {t("info")}
              </H1>
              <IconWrapper>
                <ChevronDownIcon width={12} height={12} />
              </IconWrapper>
            </div>
            <div className="eleme" onClick={() => toggleMenu("plan")}>
              <H1 small boldish>
                {t("plan")}
              </H1>
              <IconWrapper>
                <ChevronDownIcon width={12} height={12} />
              </IconWrapper>
            </div>
            <div className="socials">
              <button>
                <ActionIconWrapper onClick={openSearchModal}>
                  <Search2 width={24} height={24} />
                </ActionIconWrapper>
              </button>
              <MyModal isOpen={isSearchModalOpen} closeModal={closeSearchModal}>
                <Search handleClose={closeSearchModal} />
              </MyModal>
              <ActionIconWrapper onClick={handleFavoritesClick}>
                <Heart2 width={24} height={24} />
              </ActionIconWrapper>
            </div>
          </div>

          {/* Mobile Right Section - Burger Menu */}
          <div className="mobile-right">
            <div className="burger-menu" onClick={toggleMobileMenu}>
              <div
                className={`burger-line ${isMobileMenuOpen ? "open" : ""}`}
              ></div>
              <div
                className={`burger-line ${isMobileMenuOpen ? "open" : ""}`}
              ></div>
              <div
                className={`burger-line ${isMobileMenuOpen ? "open" : ""}`}
              ></div>
            </div>
          </div>

          {/* Mobile Menu Modal */}
          <MobileMenuModal
            isOpen={isMobileMenuOpen}
            expandedSection={expandedMobileSection}
          >
            {/* UpperNav Elements */}
            <div className="upper-nav-section">
              <div className="social-links">
                <a
                  className="social-item"
                  href={SOCIAL_MEDIA_URLS.facebook}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FacebookIcon width={16} height={16} color="#111111" />
                  <p>Facebook</p>
                </a>
                <div className="separator"></div>
                <a
                  className="social-item"
                  href={SOCIAL_MEDIA_URLS.instagram}
                  target="_blank"
                  rel="noreferrer"
                >
                  <InstagramIcon width={16} height={16} color="#111111" />
                  <p>Instagram</p>
                </a>
                <div className="separator"></div>
                <a
                  className="social-item"
                  href={SOCIAL_MEDIA_URLS.youtube}
                  target="_blank"
                  rel="noreferrer"
                >
                  <YoutubeIcon width={16} height={16} color="#111111" />
                  <p>Youtube</p>
                </a>
              </div>
            </div>

            {/* Destinations Section */}
            <div
              className={`menu-section ${
                expandedMobileSection === "destinations" ? "expanded" : ""
              }`}
            >
              <div
                className="section-title"
                onClick={() => toggleMobileSection("destinations")}
              >
                {t("destinations")}
                <IconWrapper>
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    style={{
                      transform:
                        expandedMobileSection === "destinations"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                </IconWrapper>
              </div>
              <div
                className={`section-items ${
                  expandedMobileSection === "destinations"
                    ? "expanded"
                    : "collapsed"
                }`}
              >
                {menuData.destinations[0].items.map((item, index) => (
                  <div
                    key={index}
                    className="menu-item"
                    onClick={() => handleMobileItemClick(item)}
                  >
                    <div className="item-title">{item.label}</div>
                    <div className="item-description">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Things To Do Section */}
            <div
              className={`menu-section ${
                expandedMobileSection === "thingsToDo" ? "expanded" : ""
              }`}
            >
              <div
                className="section-title"
                onClick={() => toggleMobileSection("thingsToDo")}
              >
                {t("thingsToDo")}
                <IconWrapper>
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    style={{
                      transform:
                        expandedMobileSection === "thingsToDo"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                </IconWrapper>
              </div>
              <div
                className={`section-items ${
                  expandedMobileSection === "thingsToDo"
                    ? "expanded"
                    : "collapsed"
                }`}
              >
                {menuData.thingsToDo[0].items.map((item, index) => (
                  <div
                    key={index}
                    className="menu-item"
                    onClick={() => handleMobileItemClick(item)}
                  >
                    <div className="item-title">{item.label}</div>
                    <div className="item-description">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Section */}
            <div
              className={`menu-section ${
                expandedMobileSection === "info" ? "expanded" : ""
              }`}
            >
              <div
                className="section-title"
                onClick={() => toggleMobileSection("info")}
              >
                {t("info")}
                <IconWrapper>
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    style={{
                      transform:
                        expandedMobileSection === "info"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                </IconWrapper>
              </div>
              <div
                className={`section-items ${
                  expandedMobileSection === "info" ? "expanded" : "collapsed"
                }`}
              >
                {menuData.info[0].items.map((item, index) => (
                  <div
                    key={index}
                    className="menu-item"
                    onClick={() => handleMobileItemClick(item)}
                  >
                    <div className="item-title">{item.label}</div>
                    <div className="item-description">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan Section */}
            <div
              className={`menu-section ${
                expandedMobileSection === "plan" ? "expanded" : ""
              }`}
            >
              <div
                className="section-title"
                onClick={() => toggleMobileSection("plan")}
              >
                {t("plan")}
                <IconWrapper>
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    style={{
                      transform:
                        expandedMobileSection === "plan"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                </IconWrapper>
              </div>
              <div
                className={`section-items ${
                  expandedMobileSection === "plan" ? "expanded" : "collapsed"
                }`}
              >
                {menuData.plan[0].items.map((item, index) => (
                  <div
                    key={index}
                    className="menu-item"
                    onClick={() => handleMobileItemClick(item)}
                  >
                    <div className="item-title">{item.label}</div>
                    <div className="item-description">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Language Switcher Section */}
            <div
              className={`menu-section ${
                expandedMobileSection === "language" ? "expanded" : ""
              }`}
            >
              <div
                className="section-title"
                onClick={() => toggleMobileSection("language")}
              >
                {t("language") || "Language"} ({SUPPORTED_LOCALS[local]})
                <IconWrapper>
                  <ChevronDownIcon
                    width={12}
                    height={12}
                    style={{
                      transform:
                        expandedMobileSection === "language"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                </IconWrapper>
              </div>
              <div
                className={`section-items ${
                  expandedMobileSection === "language"
                    ? "expanded"
                    : "collapsed"
                }`}
              >
                {Object.keys(SUPPORTED_LOCALS).map((langCode) => (
                  <div
                    key={langCode}
                    className={`menu-item ${
                      local === langCode ? "active" : ""
                    }`}
                    onClick={() => handleLanguageChange(langCode)}
                  >
                    <div className="item-title">
                      {SUPPORTED_LOCALS[langCode]}
                    </div>
                    <div className="item-description">
                      {local === langCode
                        ? t("currentLanguage") || "Current"
                        : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </MobileMenuModal>
        </Wrapper>
      </div>

      {/* Dropdown Menus - Outside navbar wrapper to avoid stacking context issues */}
      <CustomMenu
        open={menuStates.destinations}
        setOpen={(isOpen) => closeMenu("destinations")}
        local={local}
        menuItems={menuData.destinations}
      />
      <CustomMenu
        open={menuStates.thingsToDo}
        setOpen={(isOpen) => closeMenu("thingsToDo")}
        local={local}
        menuItems={menuData.thingsToDo}
      />
      <CustomMenu
        open={menuStates.info}
        setOpen={(isOpen) => closeMenu("info")}
        local={local}
        menuItems={menuData.info}
      />
      <CustomMenu
        open={menuStates.plan}
        setOpen={(isOpen) => closeMenu("plan")}
        local={local}
        menuItems={menuData.plan}
      />
    </>
  );
}

export default NewNavbar;
